import type { RosterFilter } from 'components/pages/GroupDetail/parts/StudentRosterFilters';
import type { IGroupRosterRow } from 'components/pages/GroupDetail/parts/rosterTypes';

/**
 * Pure roster logic for the GroupDetail Students tab — filtering,
 * searching, sorting and debtor selection.
 *
 * This module is intentionally decoupled from React/Apollo so the
 * behaviour can be property-tested in isolation. It operates on a
 * minimal structural shape (`IRosterRowLike`) rather than the concrete
 * GraphQL row, so callers can pass the real `IGroupRosterRow`
 * (`GroupDetailQuery.agreements[]`) while tests can pass plain objects.
 *
 * The filter/search rules mirror the `matches` / `passesFilter` helpers
 * that previously lived inline in `tabs/StudentsTab.tsx` — keep the two
 * in sync if either side changes.
 */

/** The user fields the roster search reads. */
interface IRosterUserLike {
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
}

/** The student wrapper the roster reads. */
interface IRosterStudentLike {
  user?: IRosterUserLike | null;
}

/**
 * Minimal structural shape of a roster row this util depends on. The
 * production `IGroupRosterRow` carries far more, but constraining to this
 * subset keeps the util pure and trivially testable with fixtures.
 */
export interface IRosterRowLike {
  agreementTitle?: string | null;
  currency?: string | null;
  budgetAmount?: number | string | null;
  paidAmount?: number | string | null;
  terminated?: boolean | null;
  student: IRosterStudentLike;
}

/**
 * Compile-time helper that resolves to `TActual` only while it is
 * assignable to `TExpected`; otherwise the constraint fails and the
 * alias below stops type-checking.
 */
type AssertAssignable<TActual extends TExpected, TExpected> = TActual;

/**
 * Compile-time contract: the production roster row
 * (`IGroupRosterRow`, sourced from `GroupDetailQuery.agreements[]`) must
 * stay assignable to the structural shape this util needs. If
 * `rosterTypes` drifts away from these fields, this alias fails to
 * type-check, surfacing the breakage here rather than at distant call
 * sites. The verified concrete type is re-exported for convenience.
 */
export type GroupRosterRow = AssertAssignable<IGroupRosterRow, IRosterRowLike>;

/** Sortable roster columns. */
export type RosterSortKey = 'debt' | 'percentPaid' | 'name' | 'status';

/** Sort direction. */
export type SortDir = 'asc' | 'desc';

/** A column + direction selection for the roster. */
export interface IRosterSort {
  key: RosterSortKey;
  dir: SortDir;
}

/**
 * Coerce a `CustomFloat` (typed `any`, sometimes serialised as a string)
 * into a finite number, defaulting to 0 — mirroring the existing roster
 * money coercion.
 */
const toAmount = (value: unknown): number => Number(value) || 0;

/** Outstanding debt for a row: `max(0, budget - paid)`. */
const debtOf = (row: IRosterRowLike): number =>
  Math.max(0, toAmount(row.budgetAmount) - toAmount(row.paidAmount));

/** Paid-to-budget ratio (0 when there is no budget). */
const percentPaidOf = (row: IRosterRowLike): number => {
  const budget = toAmount(row.budgetAmount);
  if (budget <= 0) return 0;
  return toAmount(row.paidAmount) / budget;
};

/** Student full name (first/middle/last), empty string when absent. */
const fullNameOf = (row: IRosterRowLike): string =>
  [
    row.student.user?.firstName,
    row.student.user?.middleName,
    row.student.user?.lastName,
  ]
    .filter(Boolean)
    .join(' ');

/** Status rank: active (0) sorts before terminated (1). */
const statusRank = (row: IRosterRowLike): number => (row.terminated ? 1 : 0);

/** Per-key ascending comparators. */
const COMPARATORS: Record<
  RosterSortKey,
  (a: IRosterRowLike, b: IRosterRowLike) => number
> = {
  debt: (a, b) => debtOf(a) - debtOf(b),
  percentPaid: (a, b) => percentPaidOf(a) - percentPaidOf(b),
  name: (a, b) => fullNameOf(a).localeCompare(fullNameOf(b)),
  status: (a, b) => statusRank(a) - statusRank(b),
};

/** Case-insensitive search across name/email/phone/agreement title. */
const matches = (row: IRosterRowLike, query: string): boolean => {
  if (!query) return true;
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const { user } = row.student;
  const haystack = [
    user?.firstName,
    user?.middleName,
    user?.lastName,
    user?.email,
    user?.phone,
    row.agreementTitle,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
};

/** Filter predicate for the roster filter chips. */
const passesFilter = (row: IRosterRowLike, filter: RosterFilter): boolean => {
  if (filter === 'all') return true;
  const terminated = Boolean(row.terminated);
  if (filter === 'active') return !terminated;
  if (filter === 'terminated') return terminated;
  if (filter === 'debtors') return !terminated && debtOf(row) > 0;
  return true;
};

/**
 * Apply the active filter chip and the search query to a roster. Returns
 * a new array containing only the rows that pass both — the caller feeds
 * the result into {@link sortRoster} so sorting reorders visible rows
 * only.
 */
export function filterAndSearch<T extends IRosterRowLike>(
  rows: T[],
  filter: RosterFilter,
  query: string,
): T[] {
  return rows.filter((row) => passesFilter(row, filter) && matches(row, query));
}

/**
 * Sort the rows it is given by `sort.key`. A stable, pure permutation of
 * its input: the original array is never mutated, ties preserve their
 * incoming order for `asc`, and `desc` is the exact inverse of `asc`.
 *
 * Sorting only reorders the rows passed in — callers hand it the already
 * filtered + searched rows so it touches visible rows only. A `null`
 * sort returns a copy in the incoming order.
 */
export function sortRoster<T extends IRosterRowLike>(
  rows: T[],
  sort: IRosterSort | null,
): T[] {
  if (!sort) return rows.slice();
  const compare = COMPARATORS[sort.key];
  const ascending = rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const diff = compare(a.row, b.row);
      return diff !== 0 ? diff : a.index - b.index;
    })
    .map((entry) => entry.row);
  return sort.dir === 'asc' ? ascending : ascending.reverse();
}

/**
 * Select the rows that currently carry debt — exactly those whose
 * `max(0, budget - paid)` is greater than zero — used as the bulk
 * debtor-reminder target set.
 */
export function selectDebtors<T extends IRosterRowLike>(rows: T[]): T[] {
  return rows.filter((row) => debtOf(row) > 0);
}
